import type { MouseEvent } from 'react';

import type { CourseCardItem } from '../../resources/@types/courseSchema';
import { useCart } from './useCart';
import { useLikes } from './useLikes';

interface UseCourseCardActionsProps {
  invalidatedQueryKey: (string | unknown)[];
  isAuthorized: boolean;
  /** 없으면 비로그인 클릭을 그대로 삼킨다 */
  onUnauthorized?: () => void;
  onAddLike?: (course: CourseCardItem) => void;
  onAddToCart?: (course: CourseCardItem, cartId?: number) => void;
}

export function useCourseCardActions({
  invalidatedQueryKey,
  isAuthorized,
  onUnauthorized,
  onAddLike,
  onAddToCart,
}: UseCourseCardActionsProps) {
  const { addLikeToCourse, removeLikeFromCourse } = useLikes({ invalidatedQueryKey });
  const { addToCart, removeFromCart } = useCart({ invalidatedQueryKey });

  const guard = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!isAuthorized) {
      onUnauthorized?.();

      return false;
    }

    return true;
  };

  const onClickLikeButton =
    (course: CourseCardItem) => async (event: MouseEvent<HTMLButtonElement>) => {
      if (!guard(event)) {
        return;
      }

      /*
       * mutateAsync 는 실패를 reject 로 알린다. 이 핸들러의 반환 promise 는 카드가 받아 두고 끝이라
       * 여기서 삼키지 않으면 unhandled rejection 이 된다. 실패 알림은 useLikes·useCart 가 띄운다.
       */
      try {
        if (course.hasMyReaction) {
          await removeLikeFromCourse(course.id);

          return;
        }

        await addLikeToCourse(course.id);
        onAddLike?.(course);
      } catch {
        return;
      }
    };

  const onClickCartButton =
    (course: CourseCardItem) => async (event: MouseEvent<HTMLButtonElement>) => {
      if (!guard(event)) {
        return;
      }

      try {
        if (course.cart.isInCart) {
          await removeFromCart({ courseIds: [course.id] });

          return;
        }

        const response = await addToCart({ courseIds: [course.id] });
        onAddToCart?.(course, response.data.items.map(({ cartId }) => cartId)[0]);
      } catch {
        return;
      }
    };

  return { onClickLikeButton, onClickCartButton };
}
